import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import LogoSpinner from '../../Components/Common/LogoSpinner';

const UserDemographics = ({ dateRange }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ageData, setAgeData] = useState(null);
    const [genderData, setGenderData] = useState(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const authToken = JSON.parse(sessionStorage.getItem('authUser'))?.token;

            if (!authToken) {
                throw new Error('Authentication token not found');
            }

            // Fetch both distributions in parallel
            const [ageResponse, genderResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL1}analytics/dashboard/age-distribution`, {
                    params: {
                        start_date: dateRange.start.toISOString(),
                        end_date: dateRange.end.toISOString()
                    },
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                }),
                axios.get(`${process.env.REACT_APP_API_URL1}analytics/dashboard/gender-distribution`, {
                    params: {
                        start_date: dateRange.start.toISOString(),
                        end_date: dateRange.end.toISOString()
                    },
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                })
            ]);

            if (ageResponse.data) {
                const ageDistribution = ageResponse.data.distribution;
                setAgeData({
                    metadata: ageResponse.data.metadata,
                    distribution: {
                        age_groups: ageDistribution.age_groups,
                        man: ageDistribution.Man,
                        woman: ageDistribution.Woman,
                        nonbinary: ageDistribution.Nonbinary
                    }
                });
            }

            if (genderResponse.data) {
                setGenderData(genderResponse.data);
            }
        } catch (error) {
            console.error('Error fetching demographics data:', error);
            setError('Failed to load demographics data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 300000); // Update every 5 minutes
        return () => clearInterval(interval);
    }, [dateRange]);

    const getAgeOption = () => ({
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            formatter: (params) => {
                let total = 0;
                let tooltipContent = `${params[0].axisValue}<br/>`;
                
                params.forEach(param => {
                    total += param.value;
                    tooltipContent += `${param.marker} ${param.seriesName}: ${param.value.toLocaleString()}<br/>`;
                });
                
                tooltipContent += `<strong>Total: ${total.toLocaleString()}</strong>`;
                return tooltipContent;
            }
        },
        legend: {
            data: ['Man', 'Woman', 'Nonbinary']
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            boundaryGap: [0, 0.01]
        },
        yAxis: {
            type: 'category',
            data: ageData?.distribution.age_groups || []
        },
        series: [
            {
                name: 'Man',
                type: 'bar',
                stack: 'total',
                data: ageData?.distribution.man || [],
                itemStyle: { color: '#4467EF' }
            },
            {
                name: 'Woman',
                type: 'bar',
                stack: 'total',
                data: ageData?.distribution.woman || [],
                itemStyle: { color: '#FF6B6B' }
            },
            {
                name: 'Nonbinary',
                type: 'bar',
                stack: 'total',
                data: ageData?.distribution.nonbinary || [],
                itemStyle: { color: '#37B9F1' }
            }
        ]
    });

    const getGenderOption = () => ({
        tooltip: {
            trigger: 'item',
            formatter: (params) => {
                const details = genderData?.distribution.find(item => item.gender === params.name)?.details;
                if (details) {
                    return `
                        <strong>${params.name}</strong><br/>
                        Total: ${params.value.toLocaleString()} (${params.percent}%)<br/>
                        Active Users: ${details.active_users.toLocaleString()}<br/>
                        Premium Users: ${details.premium_users.toLocaleString()}<br/>
                        New Users (30d): ${details.new_users_30d.toLocaleString()}<br/>
                        Growth Rate: ${details.growth_rate}%
                    `;
                }
                return `${params.name}: ${params.value.toLocaleString()} (${params.percent}%)`;
            }
        },
        legend: {
            orient: 'horizontal',
            bottom: '0',
            left: 'center'
        },
        series: [
            {
                name: 'Gender Distribution',
                type: 'pie',
                radius: '50%',
                data: genderData?.distribution.map(item => ({
                    value: item.count,
                    name: item.gender,
                    itemStyle: {
                        color: item.gender === 'Man' ? '#4467EF' :
                               item.gender === 'Woman' ? '#FF6B6B' : '#37B9F1'
                    }
                })) || [],
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                },
                label: {
                    show: true,
                    formatter: '{b}\n{d}%'
                }
            }
        ]
    });

    return (
        <Card>
            <CardHeader>
                <h4 className="card-title mb-0">User Demographics</h4>
            </CardHeader>
            <CardBody>
                {loading ? (
                    <div className="text-center py-5">
                        <LogoSpinner message="Loading demographic data..." />
                    </div>
                ) : error ? (
                    <div className="text-center py-5 text-danger">
                        <i className="ri-error-warning-line fs-3 mb-3 d-block"></i>
                        {error}
                    </div>
                ) : (
                    <Row>
                        <Col md={6}>
                            <h5 className="text-center mb-3">Age Distribution</h5>
                            <ReactECharts 
                                option={getAgeOption()} 
                                style={{ height: "300px" }}
                                opts={{ renderer: 'svg' }}
                            />
                        </Col>
                        <Col md={6}>
                            <h5 className="text-center mb-3">Gender Distribution</h5>
                            <ReactECharts 
                                option={getGenderOption()} 
                                style={{ height: "300px" }}
                                opts={{ renderer: 'svg' }}
                            />
                        </Col>
                    </Row>
                )}
            </CardBody>
        </Card>
    );
};

export default UserDemographics; 